import React, { useState } from 'react';

/**
 * FileUploadModal Component
 * Upload dialog supporting destination folder selection and AI classification preview
 */
export default function FileUploadModal({
    isOpen,
    onClose,
    currentProject,
    currentFolder,
    onUploadSuccess,
}) {
    const [selectedRawFile, setSelectedRawFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [targetFolderCode, setTargetFolderCode] = useState(currentFolder?.code || '01');
    const [subfolderCategory, setSubfolderCategory] = useState('');
    const [revisionRound, setRevisionRound] = useState('Revision 01');
    const [variationName, setVariationName] = useState('');
    const [category, setCategory] = useState('Production File');
    const [revisionTag, setRevisionTag] = useState('v1.0');
    const [description, setDescription] = useState('');
    const [changeNotes, setChangeNotes] = useState('');
    const [isSimulatingAI, setIsSimulatingAI] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedRawFile(file);
            setFileName(file.name);
            setIsSimulatingAI(true);
            // Auto-detect suggestion
            setTimeout(() => {
                const lower = file.name.toLowerCase();
                if (lower.includes('logo') || lower.includes('rev') || lower.includes('v1') || lower.includes('v2')) {
                    setTargetFolderCode('03');
                    setRevisionRound('Revision 01');
                    setCategory('Production Revision');
                } else if (lower.includes('sow') || lower.includes('brief') || lower.includes('spec') || lower.includes('req')) {
                    setTargetFolderCode('01');
                    setCategory('Requirement Document');
                } else if (lower.includes('deliver') || lower.includes('final') || lower.includes('master')) {
                    setTargetFolderCode('05');
                    setCategory('Production Deliverable');
                }
                setIsSimulatingAI(false);
            }, 300);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedRawFile && !fileName) return;

        setIsSubmitting(true);
        try {
            const formData = new FormData();
            if (selectedRawFile) {
                formData.append('file', selectedRawFile);
            }
            formData.append('projectId', currentProject?.id || '');
            formData.append('folderCode', targetFolderCode);
            formData.append('subfolderCategory', subfolderCategory);
            formData.append('revisionRound', targetFolderCode === '03' ? revisionRound : '');
            formData.append('variationName', variationName);
            formData.append('version', revisionTag);
            formData.append('category', category);
            formData.append('description', description);
            formData.append('changeNotes', changeNotes);

            await onUploadSuccess?.(formData, {
                fileName,
                rawFile: selectedRawFile,
                folderCode: targetFolderCode,
                subfolderCategory,
                revisionRound,
                variationName,
                category,
                version: revisionTag,
                description,
                changeNotes,
            });
            onClose?.();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-[#0b1c30]/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal Dialog */}
            <div className="file-upload-modal relative w-full max-w-2xl bg-white dark:bg-[#141c2b] rounded-2xl shadow-2xl overflow-hidden border border-[#c1c6d7] dark:border-[#344258] animate-in fade-in zoom-in-95">
                {/* Header */}
                <div className="px-6 py-4 border-b border-[#c1c6d7] dark:border-[#344258] flex items-center justify-between bg-slate-50/60 dark:bg-[#182336]">
                    <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-[#0070F3]">
                            <span className="material-symbols-outlined text-2xl">
                                upload_file
                            </span>
                        </span>
                        <div>
                            <h3 className="font-bold text-base text-[#0b1c30] dark:text-[#eaf1ff]">
                                Upload Project File
                            </h3>
                            <p className="text-xs text-[#727786] dark:text-[#9aa5bc]">
                                Upload assets to project lifecycle folders with standardized naming & revision tags
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[#727786] hover:text-[#0b1c30] dark:hover:text-white p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                        <span className="material-symbols-outlined text-xl">close</span>
                    </button>
                </div>

                {/* Form Body */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4 text-[15px]">
                    {/* Destination Folder Selector */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-[#eff4ff] dark:bg-[#1a2538] rounded-xl border border-[#c1c6d7]/70 dark:border-[#344258]">
                        <div>
                            <label className="text-[#555d73] dark:text-[#9aa5bc] block text-xs font-bold uppercase tracking-wide mb-1.5">
                                Target Lifecycle Folder:
                            </label>
                            <select
                                value={targetFolderCode}
                                onChange={(e) => setTargetFolderCode(e.target.value)}
                                className="w-full bg-white dark:bg-[#141c2b] border border-[#c1c6d7] dark:border-[#344258] rounded-lg px-3 py-2.5 text-[15px] font-semibold text-[#0b1c30] dark:text-[#eaf1ff] focus:ring-2 focus:ring-[#0070F3] outline-none shadow-2xs"
                            >
                                <option value="01">01 - Requirements</option>
                                <option value="02">02 - Resources</option>
                                <option value="03">03 - Revisions</option>
                                <option value="04">04 - Meeting Notes</option>
                                <option value="05">05 - Deliverables</option>
                            </select>
                        </div>

                        {targetFolderCode === '02' && (
                            <div>
                                <label className="text-[#555d73] dark:text-[#9aa5bc] block text-xs font-bold uppercase tracking-wide mb-1.5">
                                    Resource Subcategory:
                                </label>
                                <select
                                    value={subfolderCategory}
                                    onChange={(e) => setSubfolderCategory(e.target.value)}
                                    className="w-full bg-white dark:bg-[#141c2b] border border-[#c1c6d7] dark:border-[#344258] rounded-lg px-3 py-2.5 text-[15px] text-[#0b1c30] dark:text-[#eaf1ff] focus:ring-2 focus:ring-[#0070F3] outline-none shadow-2xs"
                                >
                                    <option value="">General Resources</option>
                                    <option value="Content">Content</option>
                                    <option value="Technical Documentation">Technical Documentation</option>
                                    <option value="Images">Images</option>
                                    <option value="Design Files">Design Files</option>
                                    <option value="Samples">Samples</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        )}

                        {targetFolderCode === '03' && (
                            <div>
                                <label className="text-[#555d73] dark:text-[#9aa5bc] block text-xs font-bold uppercase tracking-wide mb-1.5">
                                    Revision Round:
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. Revision 01"
                                    value={revisionRound}
                                    onChange={(e) => setRevisionRound(e.target.value)}
                                    className="w-full bg-white dark:bg-[#141c2b] border border-[#c1c6d7] dark:border-[#344258] rounded-lg px-3 py-2.5 text-[15px] text-[#0b1c30] dark:text-[#eaf1ff] focus:ring-2 focus:ring-[#0070F3] outline-none shadow-2xs"
                                />
                            </div>
                        )}
                    </div>

                    {/* File Dropzone */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wide text-[#555d73] dark:text-[#9aa5bc] mb-1.5">
                            Select File
                        </label>
                        <div className="border-2 border-dashed border-[#c1c6d7] dark:border-[#344258] hover:border-[#0070F3] rounded-xl p-6 text-center transition-colors relative cursor-pointer bg-slate-50/50 dark:bg-[#182336]/40">
                            <input
                                type="file"
                                onChange={handleFileChange}
                                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                required={!fileName}
                            />
                            <span className="material-symbols-outlined text-4xl text-[#0070F3] mb-1.5 block">
                                cloud_upload
                            </span>
                            <p className="font-semibold text-[15px] text-[#0b1c30] dark:text-[#eaf1ff]">
                                {fileName ? (
                                    <span className="text-[#0070F3] font-bold text-base">{fileName}</span>
                                ) : (
                                    'Drop files here or click to browse'
                                )}
                            </p>
                            <p className="text-sm text-[#727786] dark:text-[#9aa5bc] mt-1">
                                Supports PDF, Images, Excel, CAD, Video (up to 100MB)
                            </p>
                        </div>
                    </div>

                    {/* AI Classification Pill */}
                    {isSimulatingAI && (
                        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-[#e5eeff] dark:bg-[#212f45] text-[#0070F3] text-sm font-medium animate-pulse">
                            <span className="material-symbols-outlined text-lg">auto_awesome</span>
                            <span>Auto-classifying folder destination & revision tag...</span>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wide text-[#555d73] dark:text-[#9aa5bc] mb-1.5">
                                Version Tag
                            </label>
                            <input
                                type="text"
                                value={revisionTag}
                                onChange={(e) => setRevisionTag(e.target.value)}
                                className="w-full bg-[#eff4ff] dark:bg-[#1a2538] border border-[#c1c6d7] dark:border-[#344258] rounded-lg px-3 py-2.5 text-[15px] text-[#0b1c30] dark:text-[#eaf1ff] focus:ring-2 focus:ring-[#0070F3] focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wide text-[#555d73] dark:text-[#9aa5bc] mb-1.5">
                                File Category
                            </label>
                            <input
                                type="text"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="w-full bg-[#eff4ff] dark:bg-[#1a2538] border border-[#c1c6d7] dark:border-[#344258] rounded-lg px-3 py-2.5 text-[15px] text-[#0b1c30] dark:text-[#eaf1ff] focus:ring-2 focus:ring-[#0070F3] focus:outline-none"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wide text-[#555d73] dark:text-[#9aa5bc] mb-1.5">
                            Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Briefly describe what this file represents..."
                            className="w-full bg-[#eff4ff] dark:bg-[#1a2538] border border-[#c1c6d7] dark:border-[#344258] rounded-lg p-3 text-[15px] text-[#0b1c30] dark:text-[#eaf1ff] focus:ring-2 focus:ring-[#0070F3] focus:outline-none h-20 resize-none leading-relaxed"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wide text-[#555d73] dark:text-[#9aa5bc] mb-1.5">
                            Change Notes
                        </label>
                        <textarea
                            value={changeNotes}
                            onChange={(e) => setChangeNotes(e.target.value)}
                            placeholder="Specify what was modified in this revision..."
                            className="w-full bg-[#eff4ff] dark:bg-[#1a2538] border border-[#c1c6d7] dark:border-[#344258] rounded-lg p-3 text-[15px] text-[#0b1c30] dark:text-[#eaf1ff] focus:ring-2 focus:ring-[#0070F3] focus:outline-none h-20 resize-none leading-relaxed"
                        />
                    </div>

                    {/* Footer Actions */}
                    <div className="pt-4 border-t border-[#c1c6d7] dark:border-[#344258] flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg text-[15px] font-semibold text-[#727786] hover:bg-[#eff4ff] dark:hover:bg-[#1a2538] transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!fileName}
                            className="px-6 py-2.5 rounded-lg text-[15px] font-bold bg-[#0070F3] hover:bg-[#0059c5] text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-98"
                        >
                            Upload File
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
